import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const BreederProfile = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [breeder, setBreeder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL;
  const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:4000';

  useEffect(() => {
    const fetchBreeder = async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/breeder/${id}`);
        if (!res.ok) throw new Error('Breeder not found');
        const data = await res.json();
        setBreeder(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchBreeder();
  }, [id, API_URL]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
    </div>
  );

  if (error || !breeder) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center space-y-4">
        <p className="text-xl font-bold">Breeder not found</p>
        <button onClick={() => navigate('/search')}
          className="px-6 py-3 bg-primary text-white rounded-full font-bold">
          Back to Marketplace
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background text-slate-900 font-body antialiased">
      {/* Navigation */}
      <Navbar variant="default" />

      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative h-[85vh] min-h-[700px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img alt="Rajasthan Ranch at Sunset" className="w-full h-full object-cover" src="https://images.unsplash.com/photo-1500595046743-cd271d694d30?w=1600" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0.1) 100%)' }}></div>
          </div>
          
          <div className="relative z-10 w-full max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-12 lg:px-12 grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-8 text-center sm:text-left">
                <div className="relative flex-shrink-0">
                  <div className="w-32 h-32 lg:w-40 lg:h-40 rounded-full border-4 border-white/30 p-1 overflow-hidden shadow-2xl" style={{ backdropFilter: 'blur(4px)' }}>
                    <img alt={breeder.name} className="w-full h-full object-cover rounded-full" src={breeder.profile_image ? `${BASE_URL}${breeder.profile_image}` : `https://ui-avatars.com/api/?name=${breeder.name}&size=200&background=random`} />
                  </div>
                  {true && (
                    <div className="absolute -bottom-1 right-2 bg-emerald-400 text-white w-8 h-8 lg:w-10 lg:h-10 rounded-full flex items-center justify-center border-4 border-white shadow-lg">
                      <span className="material-symbols-outlined text-sm lg:text-base" style={{ fontVariationSettings: "'FILL' 1" }}>verified</span>
                    </div>
                  )}
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-9xl font-headline font-extrabold text-white tracking-tight leading-none">
                  {breeder.name.split(' ')[0]} <br/> 
                  <span style={{ color: '#06A788' }}>{breeder.name.split(' ')[1]}</span>
                </h1>
              </div>
              <p className="flex items-center gap-2 text-white/80 font-medium text-lg">
                <span className="material-symbols-outlined text-emerald-400">location_on</span>
                {breeder.city}, {breeder.state} • Established {new Date(breeder.member_since).getFullYear()}
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4 pt-4 w-full sm:w-auto">
                <button className="bg-white text-primary px-8 py-4 rounded-full font-bold flex items-center gap-3 shadow-2xl hover:bg-slate-50 transition-all">
                  <span className="material-symbols-outlined">mail</span> Send Message
                </button>
                <button
                  onClick={() => {
                    let phone = breeder.phone?.replace(/\D/g, '');
                    if (!phone) { alert('Phone number not available.'); return; }
                    if (!phone.startsWith('91')) phone = '91' + phone;
                    const msg = encodeURIComponent(`Hi ${breeder.name}, I'm interested in your livestock listings on PashuBazaar.`);
                    window.open(`https://wa.me/${phone}?text=${msg}`, '_blank');
                  }}
                  className="bg-[#25D366] text-white px-8 py-4 rounded-full font-bold flex items-center gap-3 shadow-2xl hover:scale-105 transition-transform">
                  <img alt="WA" className="w-6 h-6" src="https://lh3.googleusercontent.com/aida-public/AB6AXuCsRoagCqXow9bkrHVRE8t3rye8EB3AjYOjrauzNOsQXokwvzF4wrPJEmYzGcliZpgP2SHH6ielAy5YCOut32ZSPF_3ErBfybRO8yqn7hhlOZOxctrDH2j6Sw0dAzCJqIaRYqBkkrA_4iD3M8-E2839NN1dVks_HYgeOwUZZs4lzmiDCluEtlIISn-rAM54JYN4sP3SecXEZt11ZWaZoA7L2jy1vkZ7nAxvQSlLO3FB4EA8ektQVDD8nKHOtdyooLZr9TKpqTRbniSa" />
                  WhatsApp Breeder
                </button>
              </div>
            </div>

            {/* Stats Card */}
            <div className="lg:justify-self-end">
              <div className="p-6 sm:p-10 rounded-3xl w-full max-w-md text-white space-y-8" style={{ background: 'rgba(255, 255, 255, 0.75)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.3)', boxShadow: '0 30px 60px -15px rgba(0, 107, 86, 0.08)' }}>
                <h3 className="text-xs font-bold text-primary uppercase tracking-[0.3em]">Performance Indices</h3>
                <div className="grid grid-cols-2 gap-8">
                  <div>
                    <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-1">Legacy</p>
                    <p className="text-4xl font-headline font-bold text-slate-900">{new Date().getFullYear() - new Date(breeder.member_since).getFullYear()}<span className="text-primary text-2xl">Yrs</span></p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-1">Global Sales</p>
                    <p className="text-4xl font-headline font-bold text-slate-900">{breeder.total_sales}<span className="text-primary text-2xl">+</span></p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-1">Inventory</p>
                    <p className="text-4xl font-headline font-bold text-slate-900">{breeder.live_inventory}<span className="text-primary text-2xl">Live</span></p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-1">User Rating</p>
                    <div className="flex items-center gap-1">
                      <p className="text-4xl font-headline font-bold text-slate-900">4.9</p>
                      <span className="material-symbols-outlined text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                    </div>
                  </div>
                </div>
                <div className="pt-6 border-t border-white/30">
                  <div className="flex items-center gap-3">
                    <div className="flex -space-x-2">
                      <div className="w-8 h-8 rounded-full border-2 border-white bg-slate-200"></div>
                      <div className="w-8 h-8 rounded-full border-2 border-white bg-slate-300"></div>
                      <div className="w-8 h-8 rounded-full border-2 border-white bg-slate-400"></div>
                    </div>
                    <p className="text-xs font-medium text-slate-600">Joined by 400+ other serious breeders</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Heritage Section */}
        <section className="py-16 sm:py-24 lg:py-32 max-w-screen-2xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-20 items-center">
            <div className="lg:col-span-6 space-y-10">
              <div className="space-y-4">
                <span className="text-primary font-bold tracking-[0.4em] uppercase text-xs block">Portfolio Heritage</span>
                <h2 className="text-3xl sm:text-4xl lg:text-6xl font-headline font-bold text-slate-900 leading-[1.1]">The Legacy of {breeder.businessName || breeder.name}</h2>
                <p className="text-xl text-slate-500 leading-relaxed max-w-xl font-light">
                  {breeder.address || 'Premium livestock breeder committed to quality and excellence.'}
                </p>
              </div>
              <div className="grid sm:grid-cols-2 gap-8">
                {[
                  { icon: 'genetics', title: 'Elite Genetics', description: 'Documented lineage of award-winning sires optimized for growth.' },
                  { icon: 'verified_user', title: 'Health Certified', description: 'Stringent vaccination protocols and health certifications with every acquisition.' }
                ].map((feature, idx) => (
                  <div key={idx} className="space-y-4 p-8 bg-slate-50 rounded-2xl border border-slate-100">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                      <span className="material-symbols-outlined text-primary">{feature.icon}</span>
                    </div>
                    <h4 className="text-lg font-bold text-slate-900">{feature.title}</h4>
                    <p className="text-sm text-slate-500 leading-relaxed">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-6 relative">
              <div className="aspect-[4/5] rounded-[2rem] overflow-hidden" style={{ boxShadow: '0 30px 60px -15px rgba(0, 107, 86, 0.08)' }}>
                <img alt="Breeder Portrait" className="w-full h-full object-cover" src={breeder.profile_image ? `${BASE_URL}${breeder.profile_image}` : `https://ui-avatars.com/api/?name=${breeder.name}&size=200&background=random`} />
              </div>
              <div className="absolute -bottom-10 -left-10 bg-primary p-10 rounded-[2rem] text-white max-w-xs" style={{ boxShadow: '0 30px 60px -15px rgba(0, 107, 86, 0.08)' }}>
                <span className="material-symbols-outlined text-4xl mb-4 opacity-50">format_quote</span>
                <p className="italic text-xl mb-4 leading-relaxed font-light">"We don't just sell animals; we provide the foundation for your farm's future success."</p>
                <p className="font-bold tracking-widest text-xs uppercase opacity-80">— {breeder.name}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Stock */}
        <section className="bg-slate-50 py-16 sm:py-24 lg:py-32 border-y border-slate-100">
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-12">
            <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
              <div className="space-y-3">
                <span className="text-primary font-bold tracking-[0.4em] uppercase text-xs block">Available Now</span>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-headline font-bold text-slate-900">Featured Stock</h2>
              </div>
              <button className="group flex items-center gap-3 font-bold text-primary hover:text-emerald-accent transition-all uppercase text-sm tracking-widest" onClick={() => navigate(`/search?seller_id=${breeder.id}&seller_name=${encodeURIComponent(breeder.name)}`)}>
                View Entire Catalog <span className="material-symbols-outlined group-hover:translate-x-2 transition-transform">arrow_right_alt</span>
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              {(breeder.listings || []).map((listing) => (
                <div key={listing.id} className="group bg-white rounded-3xl overflow-hidden border border-slate-100 transition-all hover:-translate-y-2 cursor-pointer" onClick={() => navigate(`/product/${listing.public_id || listing.id}`)} style={{ boxShadow: '0 30px 60px -15px rgba(0, 107, 86, 0.08)' }}>
                  <div className="relative h-[280px] sm:h-[400px]">
                    <img alt={listing.name} className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105" style={{ filter: 'grayscale(0.2)' }} src={listing.image_url
                      ? (listing.image_url.startsWith('http') ? listing.image_url : `${BASE_URL}${listing.image_url}`)
                      : 'https://placehold.co/400x400?text=No+Image'} />
                    <div className="absolute top-6 left-6 flex flex-col gap-2">
                      <span className="bg-primary/90 text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest" style={{ backdropFilter: 'blur(12px)' }}>
                        {listing.category}
                      </span>
                      {false && (
                        <span className="bg-white/90 text-primary px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest" style={{ backdropFilter: 'blur(12px)' }}>
                          {listing.badge2}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="p-8 space-y-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="text-2xl font-headline font-bold text-slate-900">{listing.name}</h4>
                        <p className="text-slate-400 text-sm mt-1">{listing.age_months} Months • {listing.breed}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-headline font-black text-primary">₹{Number(listing.price_inr).toLocaleString('en-IN')}</p>
                        <p className="text-[10px] text-slate-400 uppercase font-bold tracking-tighter">Fixed Price</p>
                      </div>
                    </div>
                    <button className="w-full py-4 rounded-full bg-slate-900 text-white font-bold text-sm tracking-widest uppercase hover:bg-primary transition-all shadow-lg" onClick={(e) => { e.stopPropagation(); navigate(`/product/${listing.public_id || listing.id}`); }}>
                      Inquire Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reviews & Location */}
        <section className="py-16 sm:py-24 lg:py-32 max-w-screen-2xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-24">
          <div className="space-y-12">
            <div className="space-y-4">
              <span className="text-primary font-bold tracking-[0.4em] uppercase text-xs block">Vetted Reviews</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-headline font-bold text-slate-900">Client Chronicles</h2>
            </div>
            <div className="space-y-8">
              {[
                { name: 'Rajesh Kumar', initials: 'RK', rating: 5, text: 'Excellent breeder, highly recommended!', date: 'Purchased Dec 2023', verified: true },
                { name: 'Suresh Meena', initials: 'SM', rating: 5, text: 'Professional operation with clear lineage documentation.', date: 'Purchased Nov 2023', verified: true }
              ].map((review, idx) => (
                <div key={idx} className="p-10 bg-white rounded-3xl border border-slate-100 space-y-6" style={{ boxShadow: '0 30px 60px -15px rgba(0, 107, 86, 0.08)' }}>
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary text-xl">
                        {review.initials}
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-900">{review.name}</h4>
                        {review.verified && (
                          <p className="text-xs font-bold text-emerald-accent uppercase tracking-widest">Verified Buyer</p>
                        )}
                      </div>
                    </div>
                    <div className="flex text-primary">
                      {[...Array(review.rating)].map((_, i) => (
                        <span key={i} className="material-symbols-outlined text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                      ))}
                    </div>
                  </div>
                  <p className="text-slate-500 italic leading-relaxed text-lg">{review.text}</p>
                  <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">{review.date}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-12">
            <div className="space-y-4">
              <span className="text-primary font-bold tracking-[0.4em] uppercase text-xs block">Global Logistics</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-headline font-bold text-slate-900">Station Location</h2>
            </div>
            <div className="relative rounded-[3rem] overflow-hidden h-[400px] sm:h-[600px]" style={{ boxShadow: '0 30px 60px -15px rgba(0, 107, 86, 0.08)' }}>
              <img alt="Map Location" className="w-full h-full object-cover" style={{ filter: 'grayscale(1) opacity(0.2) contrast(1.25)' }} src="https://images.unsplash.com/photo-1524661135-423995f22d0b?w=800" />
              <div className="absolute inset-0 flex flex-col items-center justify-center p-12 text-center space-y-6">
                <div className="bg-primary text-white w-20 h-20 rounded-3xl flex items-center justify-center" style={{ boxShadow: '0 30px 60px -15px rgba(0, 107, 86, 0.08)', animation: 'bounce 1s infinite' }}>
                  <span className="material-symbols-outlined text-4xl">location_on</span>
                </div>
                <div className="space-y-2">
                  <p className="text-2xl font-headline font-bold text-slate-900">{breeder.city}, {breeder.state}</p>
                  <p className="text-slate-500 max-w-xs mx-auto">{breeder.address || 'Scheduled viewings only. Contact for directions.'}</p>
                </div>
                <button className="bg-white text-primary px-10 py-4 rounded-full font-bold shadow-xl border border-slate-100 hover:bg-slate-50 transition-all">
                  Request Directions
                </button>
              </div>
              <div className="absolute bottom-10 right-10 flex gap-2">
                <div className="w-12 h-12 rounded-full bg-white/80 flex items-center justify-center cursor-pointer hover:bg-white transition-all" style={{ backdropFilter: 'blur(12px)' }}>
                  <span className="material-symbols-outlined">add</span>
                </div>
                <div className="w-12 h-12 rounded-full bg-white/80 flex items-center justify-center cursor-pointer hover:bg-white transition-all" style={{ backdropFilter: 'blur(12px)' }}>
                  <span className="material-symbols-outlined">remove</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <Footer variant="default" />
    </div>
  );
};

export default BreederProfile;
